import { useState } from 'react'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { BrandMark } from '../components/Brand'
import { apiRequest, authConfigured, signInDomain } from './msal'
import './SignInGate.css'

/**
 * Everything behind a sign-in.
 *
 * The register shows one organisation's compliance position — which vehicles are
 * uninsured, and since when. That is not something to leave open, so the gate
 * wraps the whole app rather than individual routes.
 *
 * It is belt to the API's braces, not a substitute for them: every endpoint
 * already requires a token, so a page reached without one simply shows nothing.
 * This exists so the user is told to sign in instead of watching an empty screen
 * fail to load.
 */
export function SignInGate({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated()
  const { instance, inProgress } = useMsal()
  const [name, setName] = useState('')

  // No Entra values in this build — a local build against a DevAuth API, where
  // the API authenticates every request as a fixed developer. Nothing to gate.
  if (!authConfigured) return <>{children}</>

  if (isAuthenticated) return <>{children}</>

  // Mid-redirect. Showing the button here would invite a second click that
  // cancels the first attempt.
  const busy = inProgress !== InteractionStatus.None

  function signIn() {
    const typed = name.trim()

    /*
      The domain is appended here so nobody has to type it.

      Microsoft's own sign-in page always wants the full address, and we cannot
      change that page. What we can do is hand it one already filled in:
      loginHint pre-populates the account, so the user types "admin" and is asked
      only for a password.

      Anything already containing @ is passed through untouched — a guest account
      from another domain has to be able to sign in too.
    */
    const hint = typed
      ? typed.includes('@')
        ? typed
        : `${typed}@${signInDomain}`
      : undefined

    void instance.loginRedirect({ ...apiRequest, loginHint: hint })
  }

  return (
    <div className="gate">
      <form
        className="gate__card"
        onSubmit={(e) => {
          e.preventDefault()
          if (!busy) signIn()
        }}
      >
        <BrandMark size={40} className="gate__mark" />

        <h1 className="gate__name">Prarambh</h1>
        <p className="gate__tag">Fleet Due Date · Compliance register</p>

        <p className="gate__why">
          This register holds vehicle compliance records and scanned certificates.
          Sign in with your work account to continue.
        </p>

        <label className="gate__field">
          <span className="eyebrow">User name</span>
          <span className="gate__input">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="admin"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              disabled={busy}
              /* eslint-disable-next-line jsx-a11y/no-autofocus */
              autoFocus
            />
            {/* Shown, not typed. The user sees the full address they are signing
                in as without having to enter it. */}
            <span className="gate__domain">@{signInDomain}</span>
          </span>
        </label>

        <button type="submit" className="gate__btn" disabled={busy}>
          {busy ? 'Signing in…' : 'Continue'}
        </button>

        <p className="gate__help">
          You will be asked for your password on the Microsoft sign-in page. No
          account? Ask your administrator — access is granted per person so the
          register can record who changed what.
        </p>
      </form>
    </div>
  )
}
